import { PIE, PIE_GRADE, RADAR, FUNNEL, TREE_MAP, SIMPLE_BUBBLE, GRAPH_BUBBLE } from 'src/components/Charts/types';
import { CORRELATION_CARD, MULTIPLE_CARD, SIMPLE_CARD } from 'src/sections/GroupCards/cardTypes';

const COLOR_PICKER_WIDTH = 224;

/**
 * Alguns gráficos compartilham da mesma lógica do quadrante em que o color picker deve ser renderizado
 * (como pie e radar), portanto, este módulo foi criado para abstrair esse comportamento e recuperar
 * as opções de translate em que o colorpicker deve consumir de acordo com o gráfico.
 */


function getPieTypeTranslateValues({ ctx, chartWidth }) {
  // Coordenada X do cursor do mouse no evento de clique em relacao ao gráfico
  const cursorXCoordinate = ctx.event.offsetX;
  const translateX = cursorXCoordinate > (0.5 * chartWidth) ? 0.25 * chartWidth - 0.5 * COLOR_PICKER_WIDTH : 0.75 * chartWidth - 0.5 * COLOR_PICKER_WIDTH;
  const translateY = 0;

  return {
    translateX,
    translateY,
  };
}

// Para o treemap e para os bubble charts da correlação, o item clicado é
// puntual (retângulo / bolha / node). Posicionamos o color picker próximo do clique,
// preferindo abrir à direita do alvo e recuando para a esquerda quando isso fizer o
// picker estourar a borda direita.
function getCursorAdjacentTranslateValues({ ctx, chartWidth }) {
  const cursorXCoordinate = (ctx.event && ctx.event.offsetX) || 0;
  const overflowsRight = cursorXCoordinate + COLOR_PICKER_WIDTH > chartWidth;
  const translateX = overflowsRight
    ? Math.max(0, cursorXCoordinate - COLOR_PICKER_WIDTH)
    : cursorXCoordinate;
  const translateY = 0;

  return {
    translateX,
    translateY,
  };
}

const translateValuesByChartType = {
  [`${SIMPLE_CARD}_${PIE}`]: getPieTypeTranslateValues,
  [`${MULTIPLE_CARD}_${PIE}`]: getPieTypeTranslateValues,
  [`${SIMPLE_CARD}_${PIE_GRADE}`]: getPieTypeTranslateValues,
  [`${SIMPLE_CARD}_${RADAR}`]: getPieTypeTranslateValues,
  [`${CORRELATION_CARD}_${TREE_MAP}`]: getCursorAdjacentTranslateValues,
  [`${CORRELATION_CARD}_${SIMPLE_BUBBLE}`]: getCursorAdjacentTranslateValues,
  [`${CORRELATION_CARD}_${GRAPH_BUBBLE}`]: getCursorAdjacentTranslateValues,
  [`${SIMPLE_CARD}_${FUNNEL}`]: ({ chartWidth }) => {
    // Posiciona o colorpicker centralizado (no exio X) no segundo quadrante do gráfico
    const translateX = 0.75 * chartWidth - 0.5 * COLOR_PICKER_WIDTH;

    // Como a posição padrão do colorpicker é centralizado no eixo Y,
    // ao setar 0, mantém o color picker centralizado ao eixo Y
    const translateY = 0;

    return {
      translateX,
      translateY,
    };
  },
  default: ({ chartWidth, x, width }) => {
    // padding em pixels da lateral esquerda do container que envolve o gráfico (card e groupcards)
    const PADDING_LEFT_OFFSET = 36;

    // Calculando quanto em pixels o colorpicker ultrapassa a bounding box no eixo x do gráfico de barras (gráfico simples)
    const colorPickerWidthOverflowOffset = chartWidth - x - width - COLOR_PICKER_WIDTH - PADDING_LEFT_OFFSET;
    const colorPickerTranslationAmount = colorPickerWidthOverflowOffset >= 0 ? 0 : colorPickerWidthOverflowOffset;

    // Redimensiona a posicao do colorpicker baseado na barra clicada
    // Se for uma barra com espaço suficiente entre a lateral esquerda da barra e o inicio do grafico
    // posiciona ele a esquerda da barra, caso contrario, à direita. Ao posicionar a direita, é necessário verificar se
    // o colorPicker ultrapassa a bounding-box do lado direito, caso ultrapasse, é feito um translate no eixo x apenas para que
    // ele se mantenha dentro da bounding-box. Com a finalidade de não ocultar a barra clicada, caso esse colorpicker transladado
    // seja transladado no eixo X a ponto de ocultar a barra selecionada, neste caso em que há esse efeito de translate no eixoX
    // para não ultrapassar a boundingbox, é feito também um translate no eixo y. Desse modo o usuario continua conseguindo ver a
    // barra ao configurar a nova cor
    const translateX = x > COLOR_PICKER_WIDTH ? x - COLOR_PICKER_WIDTH : x + width + colorPickerTranslationAmount;
    const translateY = x <= COLOR_PICKER_WIDTH && colorPickerTranslationAmount < 0 ? -65 : 0;

    return {
      translateX,
      translateY,
    };
  },
};

/**
 *
 * @param {object} ctx Objeto de contexto de um evento disparado em um gráfico
 * @param {string} identifier Tipo do gráfico atualmente manipulado
 * @param {*} chartWidth Comprimento do gráfico de referência
 * @param {*} x coordenada x da posição da barra clicada em relação ao gráfico
 * @param {*} width largura em pixels da barra clicada
 * @returns Object { translateX, translateY } que serão consumidos no color picker
 * para aplicar o redimensionamento em sua posição.
 */
export default function getTranslateStrategyByChartType(ctx, identifier, chartWidth, x, width) {
  const translateStrategy = translateValuesByChartType[identifier] || translateValuesByChartType.default;

  return translateStrategy({ ctx, chartWidth, x, width });
}
